import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Database, Play, RefreshCw, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const t = localStorage.getItem('ats_token');
  return { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' };
}

const STEPS = [
  {
    key: 'todos-to-tasks',
    title: '1. Move old To-Dos into Tasks',
    blurb: 'Every open To-Do becomes a Task, so there is one list instead of two. Reversible.',
    path: '/api/admin/cleanup/todos-to-tasks?confirm=true',
    body: { i_have_a_backup: true },
    undo: 'POST /api/admin/cleanup/todos-to-tasks/rollback?confirm=true',
  },
  {
    key: 'companies-to-clients',
    title: '2. Link Companies to Clients',
    blurb: 'Matches the old Companies records onto the Clients they belong to. Nothing is deleted.',
    path: '/api/admin/cleanup/companies-to-clients?confirm=true',
    body: { i_have_a_backup: true },
    undo: 'POST /api/admin/cleanup/companies-to-clients/unlink?confirm=true',
  },
  {
    key: 'retire-mailer',
    title: '3. Retire the dead Mailer tables',
    blurb: 'Backs up and then removes the six unused mailer_* collections. A rollback file is written first.',
    path: '/api/admin/cleanup/retire-mailer?confirm=true',
    body: { i_have_a_backup: true },
    danger: true,
  },
  {
    key: 'date-repair',
    title: '4. Put the Candidates list back in date order',
    blurb: 'Candidates brought over from JM had their upload date saved in a different format, which parked all of them at the top of the list. This rewrites the date in the normal format - the same date, nothing invented - so the newest candidates are on top again. Reversible.',
    path: '/api/admin/date-repair/apply?confirm=true',
    body: { i_have_a_backup: true },
    undo: 'POST /api/admin/date-repair/rollback?stamp=<stamp>&confirm=true',
  },
];

export default function DataCleanupPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const [results, setResults] = useState({});

  const loadStatus = async () => {
    try {
      const r = await fetch(API + '/api/admin/cleanup/status', { headers: authHeaders() });
      setStatus(await r.json());
    } catch (e) {
      setStatus({ error: String(e) });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const run = async (step) => {
    setBusy(step.key);
    try {
      const r = await fetch(API + step.path, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(step.body || {}),
      });
      const text = await r.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch (e) { parsed = text; }
      setResults((prev) => Object.assign({}, prev, { [step.key]: { code: r.status, data: parsed } }));
      loadStatus();
    } catch (e) {
      setResults((prev) => Object.assign({}, prev, { [step.key]: { code: 0, data: String(e) } }));
    }
    setBusy('');
  };

  const counts = (status && status.retire_targets) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Data Cleanup" subtitle="One-time tidy-up of the live database" />
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="w-4 h-4 text-amber-600" />
              What is still waiting
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-1">
            {status && status.todos_to_tasks && (
              <div>Open To-Dos not yet moved: <b>{status.todos_to_tasks.todos_still_open}</b></div>
            )}
            {status && status.companies_to_clients && (
              <div>Companies already linked: <b>{status.companies_to_clients.companies_linked}</b></div>
            )}
            {counts.length > 0 && (
              <div>Mailer rows to retire: <b>{counts.reduce((a, c) => a + (c.count || 0), 0).toLocaleString()}</b> across {counts.length} tables</div>
            )}
            <button onClick={loadStatus} className="text-xs text-slate-500 underline mt-2 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> refresh
            </button>
          </CardContent>
        </Card>

        {STEPS.map((step) => {
          const res = results[step.key];
          return (
            <Card key={step.key} className={step.danger ? 'border-amber-300' : ''}>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-slate-900">{step.title}</div>
                    <div className="text-sm text-slate-500 mt-1">{step.blurb}</div>
                  </div>
                  <Button
                    onClick={() => run(step)}
                    disabled={busy === step.key}
                    className={step.danger ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    data-testid={'cleanup-run-' + step.key}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {busy === step.key ? 'Running...' : 'Run'}
                  </Button>
                </div>
                {res && (
                  <pre className="text-xs bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre-wrap">
                    {'HTTP ' + res.code + '\n' + (typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2))}
                  </pre>
                )}
              </CardContent>
            </Card>
          );
        })}

        <div className="text-xs text-slate-500 flex items-center gap-2 pt-2">
          <ShieldCheck className="w-3 h-3" />
          Admin only. Step 3 writes a backup and a rollback script into _dcs_backups before it removes anything.
        </div>
      </div>
    </div>
  );
}
